import tw from 'twin.macro'
import { useRef, useEffect } from 'react'
import { trackCustomEvent } from 'gatsby-plugin-google-analytics'
import LogoHorizontal from '@/icons/logo-horizontal.svg'
import Container from '@/components/container'
import LinkComponent from '@/components/link-component'
import SelectLocale from '@/components/select-locale'
import CtaLink from '@/components/ui/cta-link'
import RichText from '@/components/ui/rich-text'
import Newsletter from '@/components/newsletter'
import { graphql, useStaticQuery } from 'gatsby'
import routes from '@/constants/routes'
import { useLocale } from '@plugins/bw-i18next'

export default function Footer({ showLinkSections = true, otherLocales = [] }) {
  const footerBottomLinksRef = useRef(null)
  const { currentContentfulCode, lang } = useLocale()

  useEffect(() => {
    const osanoLink = footerBottomLinksRef.current.querySelector('a[href$="#osano"]')

    if (osanoLink) {
      osanoLink.addEventListener('click', (event) => {
        if (window.Osano && window.Osano.cm) {
          event.preventDefault()
          window.Osano.cm.locale = lang
          window.Osano.cm.showDrawer()
        }
      })
    }
  }, [])

  const data = useStaticQuery(graphql`
    query AllContentfulSectionFooter {
      allContentfulSectionFooter(filter: { contentful_id: { eq: "4uUrzgSyym4xyL08VjqwVR" } }) {
        nodes {
          node_locale
          linkSections {
            links {
              ...CtaLink
            }
            heading
          }
          bottomText {
            ...RichText
          }
        }
      }
    }
  `)

  const { linkSections, bottomText } = data.allContentfulSectionFooter.nodes.find(
    (node: { node_locale: string }) => node.node_locale === currentContentfulCode
  )

  const renderBottomLinks = () => (
    <div
      ref={footerBottomLinksRef}
      tw="text-center lg:text-left [&>p]:(text-base mb-0) [&>p>a]:(no-underline ml-1) [&>p>a:hover]:text-primaryBlue dark:[&>p>a:hover]:text-gray-400"
    >
      <RichText body={bottomText.body} />
    </div>
  )

  const footerIconStyle = tw`hover:text-primaryBlue text-base dark:hover:text-gray-400 [&_span]:(text-[1.2rem]) xl:[&_span]:(text-[1.4rem])`

  return (
    <footer tw="bg-gray-100 text-indigoBlue [*>a]:text-indigoBlue py-10 border-t border-gray-200 dark:([*>a]:text-gray-500 text-white bg-gray-800 border-gray-800)">
      <Container>
        {showLinkSections ? (
          <div tw="flex flex-col gap-10">
            <nav tw="grid gap-8 sm:grid-cols-2 md:grid-cols-4">
              {linkSections.map((section, i) => (
                <div key={i}>
                  <h4 tw="text-2xl mb-4">{section.heading}</h4>
                  <div tw="flex flex-col gap-3">
                    {section.links.map((link) => (
                      <CtaLink
                        key={link.id}
                        to={link.link.url}
                        {...link}
                        tw="no-underline text-base hover:text-primaryBlue dark:hover:text-gray-400"
                      >
                        {link.label}
                      </CtaLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
            <Newsletter />
            <hr tw="dark:border-gray-600" />
            <div tw="flex flex-col max-lg:gap-6 gap-4 xl:gap-6 justify-between items-center lg:flex-row [&>p]:text-base">
              {renderBottomLinks()}
              <div tw="flex gap-3 xl:gap-4 flex-wrap">
                <LinkComponent
                  to="https://twitter.com/bitwarden"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on Twitter' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi" data-name="bwi-twitter">
                    &#xe9a5;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://reddit.com/r/bitwarden"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on Reddit' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi" data-name="bwi-reddit">
                    &#xe95a;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://community.bitwarden.com/"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on Community' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi " data-name="bwi-community">
                    &#xe994;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://github.com/bitwarden"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on GitHub' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi" data-name="bwi-github">
                    &#xe950;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.youtube.com/channel/UCId9a_jQqvJre0_dE2lE_Rw"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on YouTube' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi" data-name="bwi-youtube">
                    &#xe966;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.linkedin.com/company/bitwarden1"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on LinkeIn' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi" data-name="bwi-linkedin">
                    &#xe955;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.facebook.com/bitwarden/"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on Facebook' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi" data-name="bwi-facebook">
                    &#xe94d;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.instagram.com/bitwarden/"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on Instagram' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi" data-name="bwi-instagram">
                    &#xe999;
                  </span>
                </LinkComponent>
                <LinkComponent
                  rel="me"
                  to="https://fosstodon.org/@bitwarden"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on Mastodon' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi" data-name="bwi-mastodon">
                    &#xe995;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.twitch.tv/bitwardenlive"
                  onClick={() => trackCustomEvent({ category: 'main', action: 'Follow on Twitch' })}
                  css={footerIconStyle}
                >
                  <span tw="font-bwi" data-name="bwi-twitch">
                    &#xe993;
                  </span>
                </LinkComponent>
              </div>
              <div>
                <SelectLocale otherLocales={otherLocales} />
              </div>
            </div>
          </div>
        ) : (
          <div tw="flex justify-between">
            <div tw="w-40">
              <LinkComponent to={routes.index}>
                <LogoHorizontal tw="text-primaryBlue" />
              </LinkComponent>
            </div>
            {renderBottomLinks()}
          </div>
        )}
      </Container>
    </footer>
  )
}
